import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../configureStore";
import { IInstitution, updateInstitutionSteps } from "../../../redux/reducer/institution";
import { ConfigStepTranscript, newDocument, SignatureDocument } from "../../../utils/configuration";
import {
    Button,
    Checkbox,
    Col,
    Input,
    InputNumber,
    Row,
    Space,
    Switch,
    Typography,
    Card,
    Tabs,
    Alert,
    Divider,
    Empty,
} from "antd";
import { useCallback, useState, CSSProperties } from "react";
import { AuthorizationStepType, RequestForms, TranscriptParams } from "../Transcript";
import { SignatureDocumentModalForm } from "./SignatureDocumentModal";
import { LivenessConfigurationForm } from "./LivenessConfigurationForm";
import { useMsal } from "@azure/msal-react";
import { AxiosResponse } from "axios";
import { allFormTypes } from "../../../utils/transcript";
import {
    InfoCircleOutlined,
    FileProtectOutlined,
    FormOutlined,
    CheckSquareOutlined,
    EditOutlined,
    SaveOutlined,
    CalendarOutlined,
    IdcardOutlined,
    MinusOutlined,
    PlusOutlined,
    DragOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const styles: Record<string, CSSProperties> = {
    container: {
        padding: '16px',
    },
    header: {
        marginBottom: '24px',
    },
    nameOverrideSection: {
        backgroundColor: '#fafafa',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
    },
    documentCard: {
        marginBottom: '16px',
        borderRadius: '8px',
        transition: 'all 0.3s',
        cursor: 'move',
    },
    documentCardEnabled: {
        borderColor: '#1890ff',
    },
    documentCardDisabled: {
        opacity: 0.7,
        backgroundColor: '#fafafa',
    },
    documentCardDragging: {
        opacity: 0.5,
        transform: 'scale(0.98)',
    },
    documentCardDragOver: {
        borderColor: '#1890ff',
        borderWidth: '2px',
        borderStyle: 'dashed',
    },
    dragHandle: {
        fontSize: '18px',
        color: '#999',
        cursor: 'grab',
        padding: '4px 8px',
        transition: 'all 0.3s',
    },
    dragHandleActive: {
        cursor: 'grabbing',
        color: '#1890ff',
    },
    formItem: {
        padding: '12px 16px',
        borderRadius: '6px',
        marginBottom: '8px',
        backgroundColor: '#fafafa',
        transition: 'all 0.3s',
    },
    formItemSelected: {
        backgroundColor: '#e6f7ff',
        borderLeft: '3px solid #1890ff',
    },
    yearInput: {
        width: '60px',
    },
    infoAlert: {
        marginBottom: '16px',
    },
    tabIcon: {
        marginRight: '8px',
    },
};

interface AdminTranscriptParams {
    stepIndex: number;
    step: ConfigStepTranscript;
}

const DEFAULT_ORDER: AuthorizationStepType[] = ['consent', 'tia', 'liveness', 'approval'];

const Authorization = ({
    consent,
    tia,
    approval,
    liveness,
    order,
    update,
}: TranscriptParams['authorization'] & {
    update: (params: Partial<TranscriptParams>, onSuccess?: (res: AxiosResponse<IInstitution>) => void) => void;
}) => {
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION_STEPS']);
    const [minimizedDocs, setMinimizedDocs] = useState<string[]>([]);
    const [draggedStep, setDraggedStep] = useState<string | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const currentOrder = order ?? DEFAULT_ORDER;

    const toggleMinimize = (docKey: string) => {
        setMinimizedDocs(prev =>
            prev.includes(docKey)
                ? prev.filter(key => key !== docKey)
                : [...prev, docKey]
        );
    };

    const handleDragStart = (e: React.DragEvent, stepKey: string) => {
        setDraggedStep(stepKey);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (!draggedStep) return;

        const draggedIndex = currentOrder.indexOf(draggedStep as AuthorizationStepType);
        if (draggedIndex === -1) return;

        const newOrder = [...currentOrder];
        const [draggedItem] = newOrder.splice(draggedIndex, 1);
        newOrder.splice(dropIndex, 0, draggedItem);

        const authorization: TranscriptParams['authorization'] = {
            order: newOrder,
            consent,
            tia,
            approval,
            liveness,
        };

        update({ authorization });
        setDraggedStep(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedStep(null);
        setDragOverIndex(null);
    };

    const updateDocument = useCallback((document: SignatureDocument, type: 'consent' | 'tia') => {
        const authorization: TranscriptParams['authorization'] = {
            order: currentOrder,
            consent,
            tia,
            approval,
            [type]: {
                ...(type === 'tia' ? tia : consent),
                document,
            }
        };

        update({ authorization },);
    }, [consent, tia, approval, liveness, currentOrder]);

    const updateLiveness = useCallback((partial: { beforeSignature?: boolean; actionNeeded?: boolean; displayText?: string }) => {
        const authorization: TranscriptParams['authorization'] = {
            order: currentOrder,
            consent,
            tia,
            approval,
            liveness: {
                enabled: liveness?.enabled ?? false,
                beforeSignature: liveness?.beforeSignature,
                actionNeeded: liveness?.actionNeeded,
                displayText: liveness?.displayText,
                ...partial,
            }
        };

        update({ authorization },);
    }, [consent, tia, approval, liveness, currentOrder]);

    const updateEnabled = useCallback((value: boolean, type: keyof TranscriptParams['authorization']) => {
        const authorization: TranscriptParams['authorization'] = {
            order: currentOrder,
            consent,
            tia,
            approval,
            liveness,
            [type]: {
                ...((type === 'approval' ? approval : type === 'liveness' ? liveness : type === 'tia' ? tia : consent) ?? {}),
                enabled: value,
                ...((type === 'consent' || type === 'tia') && value && !(type === 'consent' ? consent : tia).document) && {
                    document: newDocument(type),
                }
            },
        };

        update(
            { authorization },
            () => {
                // if (value) setActiveKey(type);
                // else setActiveKey(undefined);
            },
        );
    }, [consent, tia, approval, liveness, currentOrder]);

    const documentsMap: Record<AuthorizationStepType, {
        key: AuthorizationStepType;
        title: string;
        description: string;
        icon: React.ReactNode;
        enabled: boolean | undefined;
        hasDocument: boolean;
        component: React.ReactNode;
    }> = {
        consent: {
            key: 'consent',
            title: 'Consent Form',
            description: 'User consent for transcript retrieval',
            icon: <FileProtectOutlined />,
            enabled: consent.enabled,
            hasDocument: consent.enabled,
            component: (
                <SignatureDocumentModalForm
                    onFinish={(values) => updateDocument(values, 'consent')}
                    document={consent.document}
                    enabled={consent.enabled}
                />
            ),
        },
        tia: {
            key: 'tia',
            title: 'TIA Form',
            description: 'Transcript Information Authorization',
            icon: <FormOutlined />,
            enabled: tia.enabled,
            hasDocument: tia.enabled,
            component: (
                <SignatureDocumentModalForm
                    onFinish={(values) => updateDocument(values, 'tia')}
                    document={tia.document}
                    enabled={tia.enabled}
                />
            ),
        },
        liveness: {
            key: 'liveness',
            title: 'Liveness Check',
            description: 'ID Verification and Liveness Check Step',
            icon: <IdcardOutlined />,
            enabled: liveness?.enabled,
            hasDocument: false,
            component: (
                <LivenessConfigurationForm
                    beforeSignature={liveness?.beforeSignature}
                    actionNeeded={liveness?.actionNeeded}
                    displayText={liveness?.displayText}
                    onUpdate={updateLiveness}
                />
            ),
        },
        approval: {
            key: 'approval',
            title: 'Borrower Approval',
            description: 'Borrower approval for transcript access',
            icon: <CheckSquareOutlined />,
            enabled: approval.enabled,
            hasDocument: false,
            component: (
                <Empty
                    description="Borrower approval configuration"
                    style={{ padding: '40px 0' }}
                />
            ),
        },
    };

    const sortedDocuments = currentOrder.map(key => documentsMap[key]);

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
                message="Authorization Documents"
                description="Configure which authorization documents are required for transcript retrieval. Drag to reorder the steps."
                type="info"
                showIcon
                closable
                style={styles.infoAlert}
            />

            {sortedDocuments.map((doc, index) => {
                const isMinimized = minimizedDocs.includes(doc.key);
                const isDragging = draggedStep === doc.key;
                const isDragOver = dragOverIndex === index;

                return (
                    <Card
                        key={doc.key}
                        draggable
                        onDragStart={(e) => handleDragStart(e, doc.key)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                            ...styles.documentCard,
                            ...(doc.enabled ? styles.documentCardEnabled : styles.documentCardDisabled),
                            ...(isDragging ? styles.documentCardDragging : {}),
                            ...(isDragOver ? styles.documentCardDragOver : {}),
                        }}
                        hoverable={doc.enabled}
                    >
                        <Row justify="space-between" align="middle" style={{ marginBottom: doc.enabled && !isMinimized ? '16px' : 0 }}>
                            <Col>
                                <Space size="large">
                                    <DragOutlined
                                        style={{
                                            ...styles.dragHandle,
                                            ...(isDragging ? styles.dragHandleActive : {}),
                                        }}
                                    />
                                    <span style={{ fontSize: '20px', color: doc.enabled ? '#1890ff' : '#999' }}>
                                        {doc.icon}
                                    </span>
                                    <div>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {doc.title}
                                        </Title>
                                        {!isMinimized && (
                                            <Text type="secondary">{doc.description}</Text>
                                        )}
                                    </div>
                                </Space>
                            </Col>
                            <Col>
                                <Space>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={isMinimized ? <PlusOutlined /> : <MinusOutlined />}
                                        onClick={() => toggleMinimize(doc.key)}
                                        title={isMinimized ? "Expand" : "Minimize"}
                                    />
                                    <Switch
                                        checked={doc.enabled}
                                        loading={loading}
                                        onChange={(checked) => updateEnabled(checked, doc.key as keyof TranscriptParams['authorization'])}
                                        checkedChildren="Enabled"
                                        unCheckedChildren="Disabled"
                                    />
                                </Space>
                            </Col>
                        </Row>

                        {doc.enabled && !isMinimized && (
                            <>
                                <Divider style={{ margin: '16px 0' }} />
                                {doc.component}
                            </>
                        )}
                    </Card>
                );
            })}
        </Space>
    );
}

export const Forms = ({
    forms,
    overrideTenantId,
    useTenant,
    type,
    update,
}: {
    forms: RequestForms[];
    type: 'Individual' | 'Business';
    useTenant: boolean;
    overrideTenantId?: string
    update: (params: Partial<TranscriptParams>, onSuccess?: (res: AxiosResponse<IInstitution>) => void) => void;
}) => {
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION_STEPS']);
    const [formTypes, setFormTypes] = useState<string[]>(forms.map(({ formName }) => formName));
    const [years, setYears] = useState<{ [formName: string]: number | null; }>(forms.reduce((a, c: RequestForms) => ({ ...a, [c.formName]: c.taxYears }), {}));
    const [otid, setOtid] = useState<string | undefined>(overrideTenantId);

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
                message="Form Configuration"
                description="Select which tax forms to request and specify the number of years for each form. Pre-Auth is automatically included if no forms are selected."
                type="info"
                showIcon
                closable
                icon={<InfoCircleOutlined />}
                style={styles.infoAlert}
            />

            <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                        <Checkbox
                            checked={useTenant}
                            onChange={(e) => {
                                update({ useTenant: e.target.checked });
                            }}
                        >
                            <Space>
                                <IdcardOutlined />
                                <span>Use Tenant ID for Requests</span>
                            </Space>
                        </Checkbox>
                    </div>

                    {useTenant && (
                        <Input
                            value={otid}
                            onChange={(e) => { setOtid(e.target.value); }}
                            placeholder="Enter override tenant ID"
                            prefix={<IdcardOutlined />}
                            addonAfter={(
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={loading}
                                    icon={<SaveOutlined />}
                                    onClick={() => { update({ overrideTenantId: otid }); }}
                                >
                                    Save
                                </Button>
                            )}
                            style={{ maxWidth: '500px' }}
                        />
                    )}
                </Space>
            </Card>
            <Card title={<Space><FormOutlined />Tax Forms Selection</Space>}>
                <Checkbox.Group
                    value={formTypes}
                    onChange={setFormTypes}
                    style={{ width: '100%' }}
                >
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                        {(type === 'Individual' ? allFormTypes.individual : allFormTypes.business).map((fn) => {
                            const isSelected = formTypes.includes(fn);
                            return (
                                <div
                                    key={`form-${fn}`}
                                    style={{
                                        ...styles.formItem,
                                        ...(isSelected ? styles.formItemSelected : {}),
                                    }}
                                >
                                    <Row align="middle" gutter={16}>
                                        <Col flex="auto">
                                            <Checkbox value={fn}>
                                                <Text strong={isSelected}>{fn}</Text>
                                            </Checkbox>
                                        </Col>
                                        <Col>
                                            <Space align="center">
                                                <CalendarOutlined style={{ color: isSelected ? '#1890ff' : '#999' }} />
                                                <Text type={isSelected ? undefined : "secondary"}>Last</Text>
                                                <InputNumber
                                                    value={years[fn] || 1}
                                                    style={styles.yearInput}
                                                    step={1}
                                                    defaultValue={1}
                                                    precision={0}
                                                    max={4}
                                                    min={1}
                                                    disabled={!isSelected}
                                                    onChange={(value) => {
                                                        setYears({
                                                            ...years,
                                                            [fn]: value,
                                                        });
                                                    }}
                                                />
                                                <Text type={isSelected ? undefined : "secondary"}>Year(s)</Text>
                                            </Space>
                                        </Col>
                                    </Row>
                                </div>
                            );
                        })}
                    </Space>
                </Checkbox.Group>

                <Divider />

                <Row justify="end">
                    <Button
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        onClick={() => {
                            update({
                                forms: formTypes.map((fn) => ({
                                    formName: fn,
                                    taxYears: years[fn] ?? 1
                                }))
                            });
                        }}
                        loading={loading}
                    >
                        Save Form Configuration
                    </Button>
                </Row>
            </Card>
        </Space>
    );
}

const Transcript = ({
    stepIndex,
    step,
}: AdminTranscriptParams) => {
    const { accounts: [account] } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const institution = useSelector<RootState, IInstitution | undefined>(({ institution: { testInstitution } }) => testInstitution);
    const [nameOverride, setNameOverride] = useState<string | undefined>(step.nameOverride);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION_STEPS']);

    const update = useCallback((updated: Partial<TranscriptParams>, onSuccess?: (res: AxiosResponse<IInstitution>) => void) => {
        if (institution) {
            const copied = structuredClone(step);
            copied.params = {
                ...step.params,
                ...updated,
            };

            const services = institution.services.map((serve, idx) => (stepIndex === idx ? copied : serve));

            dispatch(updateInstitutionSteps({
                headers: {
                    Authorization: `Bearer ${account.idToken}`,
                },
                input: {
                    id: institution.id,
                    institutionId: institution.institutionId,
                    services,
                },
                message: { success: 'Successfully updated!', error: 'Failed to update step' },
                onSuccess,
            }, 'params'))
        }
    }, [step, stepIndex, institution])

    const handleSaveNameOverride = () => {
        if (nameOverride && institution) {
            const copied = structuredClone(step);
            copied.nameOverride = nameOverride;

            const services = institution.services.map((serve, idx) => (stepIndex === idx ? copied : serve));

            dispatch(updateInstitutionSteps({
                headers: {
                    Authorization: `Bearer ${account.idToken}`,
                },
                input: {
                    id: institution.id,
                    institutionId: institution.institutionId,
                    services,
                },
                message: { success: 'Step name updated successfully!', error: 'Failed to update step name' },
            }, 'params'));
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.nameOverrideSection}>
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong>Step Display Name</Text>
                            <Input
                                value={nameOverride}
                                onChange={(e) => setNameOverride(e.target.value)}
                                placeholder="Enter custom step name (default: Transcript)"
                                prefix={<EditOutlined />}
                                size="large"
                            />
                        </Space>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            size="large"
                            loading={loading}
                            icon={<SaveOutlined />}
                            onClick={handleSaveNameOverride}
                            disabled={!nameOverride || nameOverride === step.nameOverride}
                        >
                            Update Name
                        </Button>
                    </Col>
                </Row>
            </div>

            <Tabs defaultActiveKey="authorization" size="large">
                <TabPane
                    tab={
                        <span>
                            <FileProtectOutlined style={styles.tabIcon} />
                            Authorization
                        </span>
                    }
                    key="authorization"
                >
                    <Authorization {...step.params.authorization} update={update} />
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <FormOutlined style={styles.tabIcon} />
                            Forms
                        </span>
                    }
                    key="forms"
                >
                    <Forms
                        useTenant={step.params.useTenant}
                        overrideTenantId={step.params.overrideTenantId}
                        forms={step.params.forms}
                        type="Individual"
                        update={update}
                    />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default Transcript;
